import { SEQUENCE } from '../constants/sequence';
import type { SequenceFragment, SequenceMessage, SequenceParticipant } from '../types';
import { isFragmentDescendantOf, isInsideFragmentTree } from './fragmentTree';
import { estimateSequenceMessageFootprint } from './sequenceMessageLayout';

export interface FragmentLayoutState {
  participants: SequenceParticipant[];
  messages: SequenceMessage[];
  fragments: SequenceFragment[];
}

const SEQUENCE_LABEL_SAFE_MARGIN = SEQUENCE.LABEL_SAFE_MARGIN;

export function fragmentPadTop(type: SequenceFragment['type']): number {
  return type === 'alt' ? SEQUENCE.FRAGMENT_PAD_TOP_ALT : SEQUENCE.FRAGMENT_PAD_TOP;
}

export function fragmentPadBottom(type: SequenceFragment['type']): number {
  return type === 'alt' ? SEQUENCE.FRAGMENT_PAD_BOTTOM_ALT : SEQUENCE.FRAGMENT_PAD_BOTTOM;
}

export function messageBelongsInFragment(
  message: Pick<SequenceMessage, 'sourceId' | 'targetId'>,
  fragment: SequenceFragment,
): boolean {
  if (fragment.participantIds.length === 0) return false;
  const ids = new Set(fragment.participantIds);
  return ids.has(message.sourceId) && ids.has(message.targetId);
}

export function messageYInsideFragment(messageY: number, fragment: SequenceFragment): boolean {
  return (
    messageY > fragment.startY + SEQUENCE.FRAGMENT_LABEL_HEIGHT
    && messageY < fragment.endY - SEQUENCE.FRAGMENT_OUTSIDE_GAP
  );
}

export function innermostContainingFragment(
  fragments: SequenceFragment[],
  startY: number,
  endY: number,
  options?: { excludeId?: string },
): SequenceFragment | undefined {
  const excludeId = options?.excludeId;
  const candidates = fragments.filter((fragment) => {
    if (excludeId) {
      if (fragment.id === excludeId) return false;
      if (isFragmentDescendantOf(fragment.id, excludeId, fragments)) return false;
    }
    return startY >= fragment.startY && endY <= fragment.endY;
  });
  return candidates.reduce<SequenceFragment | undefined>((best, fragment) => {
    if (!best) return fragment;
    return (fragment.endY - fragment.startY) < (best.endY - fragment.startY) ? fragment : best;
  }, undefined);
}

export function resolveMessageFragmentId(
  message: Pick<SequenceMessage, 'y' | 'sourceId' | 'targetId'>,
  fragments: SequenceFragment[],
): string | undefined {
  const candidates = fragments.filter((fragment) => (
    messageYInsideFragment(message.y, fragment)
    && messageBelongsInFragment(message, fragment)
  ));
  const innermost = candidates.reduce<SequenceFragment | undefined>((best, fragment) => {
    if (!best) return fragment;
    return (fragment.endY - fragment.startY) < (best.endY - fragment.startY) ? fragment : best;
  }, undefined);
  return innermost?.id;
}

export function resolveStickyParentFragmentId(
  fragments: SequenceFragment[],
  child: SequenceFragment,
  hysteresis = SEQUENCE.FRAGMENT_NEST_HYSTERESIS,
): string | undefined {
  const current = child.parentFragmentId
    ? fragments.find((fragment) => fragment.id === child.parentFragmentId)
    : undefined;
  if (current && !isFragmentDescendantOf(current.id, child.id, fragments)) {
    const stillInside =
      child.startY >= current.startY - hysteresis
      && child.endY <= current.endY + hysteresis;
    if (stillInside) return current.id;
  }
  return innermostContainingFragment(fragments, child.startY, child.endY, { excludeId: child.id })?.id;
}

export function applyFragmentNesting(
  state: Pick<FragmentLayoutState, 'fragments'>,
  childId: string,
  parentId: string | undefined,
): void {
  const child = state.fragments.find((fragment) => fragment.id === childId);
  if (!child) return;
  if (parentId === childId) return;
  if (parentId && isFragmentDescendantOf(parentId, childId, state.fragments)) return;

  const parent = parentId
    ? state.fragments.find((fragment) => fragment.id === parentId)
    : undefined;
  child.parentFragmentId = parent?.id;
  if (!parent) return;

  const parentSet = new Set(parent.participantIds);
  const intersected = child.participantIds.filter((id) => parentSet.has(id));
  if (intersected.length > 0) {
    child.participantIds = intersected;
  }

  parent.startY = Math.min(parent.startY, child.startY - SEQUENCE.FRAGMENT_NEST_PAD);
  parent.endY = Math.max(parent.endY, child.endY + SEQUENCE.FRAGMENT_NEST_PAD);
}

export function assignBelongingMessagesInFragment(
  state: Pick<FragmentLayoutState, 'messages' | 'fragments'>,
  fragmentId: string,
): void {
  const fragment = state.fragments.find((entry) => entry.id === fragmentId);
  if (!fragment) return;
  for (const message of state.messages) {
    if (!messageYInsideFragment(message.y, fragment)) continue;
    if (!messageBelongsInFragment(message, fragment)) continue;
    message.fragmentId = resolveMessageFragmentId(message, state.fragments);
  }
}

export function nearestParticipantIds(
  participants: Array<{ id: string; x: number }>,
  canvasX: number,
  count = 2,
): string[] {
  if (participants.length === 0) return [];
  const half = SEQUENCE.HEADER_WIDTH / 2;
  const ranked = [...participants].sort((a, b) => {
    const da = Math.abs(a.x + half - canvasX);
    const db = Math.abs(b.x + half - canvasX);
    if (da !== db) return da - db;
    return a.x - b.x;
  });
  const picked = ranked.slice(0, Math.min(count, ranked.length));
  picked.sort((a, b) => a.x - b.x);
  return picked.map((participant) => participant.id);
}

export function fragmentBoundaryGapExtra(
  upper: { fragmentId?: string },
  lower: { fragmentId?: string },
  fragments: SequenceFragment[],
): number {
  if (fragments.length === 0) return 0;
  let extra = 0;
  for (const fragment of fragments) {
    const upperInside = isInsideFragmentTree(upper.fragmentId, fragment.id, fragments);
    const lowerInside = isInsideFragmentTree(lower.fragmentId, fragment.id, fragments);
    if (!upperInside && lowerInside) {
      extra = Math.max(
        extra,
        fragmentPadTop(fragment.type) + SEQUENCE.FRAGMENT_OUTSIDE_GAP - SEQUENCE.MESSAGE_VERTICAL_BUFFER,
      );
    }
    if (upperInside && !lowerInside) {
      extra = Math.max(
        extra,
        fragmentPadBottom(fragment.type) + SEQUENCE.FRAGMENT_OUTSIDE_GAP - SEQUENCE.MESSAGE_VERTICAL_BUFFER,
      );
    }
  }
  return extra;
}

function estimateFootprint(
  participants: SequenceParticipant[],
  message: SequenceMessage,
): { above: number; below: number } {
  return estimateSequenceMessageFootprint(
    participants,
    message.label,
    message.sourceId,
    message.targetId,
    { safeMargin: SEQUENCE_LABEL_SAFE_MARGIN },
  );
}

export function clampFragmentAgainstNeighbors(
  state: FragmentLayoutState,
  fragment: SequenceFragment,
  startY: number,
  endY: number,
): { startY: number; endY: number } {
  const contained = state.messages.filter((message) =>
    isInsideFragmentTree(message.fragmentId, fragment.id, state.fragments),
  );
  const minContainedY = contained.length > 0
    ? contained.reduce((min, message) => (message.y < min ? message.y : min), Infinity)
    : startY + SEQUENCE.FRAGMENT_LABEL_HEIGHT;
  const maxContainedY = contained.length > 0
    ? contained.reduce((max, message) => (message.y > max ? message.y : max), -Infinity)
    : endY - SEQUENCE.FRAGMENT_OUTSIDE_GAP;

  const outsiders = state.messages.filter((message) =>
    !isInsideFragmentTree(message.fragmentId, fragment.id, state.fragments),
  );
  const previous = outsiders
    .filter((message) => message.y < minContainedY)
    .reduce<SequenceMessage | undefined>((best, message) => {
      if (!best || message.y > best.y) return message;
      return best;
    }, undefined);
  const next = outsiders
    .filter((message) => message.y > maxContainedY)
    .reduce<SequenceMessage | undefined>((best, message) => {
      if (!best || message.y < best.y) return message;
      return best;
    }, undefined);

  let nextStartY = startY;
  let nextEndY = endY;

  if (previous) {
    const footprint = estimateFootprint(state.participants, previous);
    const minStart = Math.max(
      previous.y + footprint.below + SEQUENCE.FRAGMENT_OUTSIDE_GAP,
      previous.y + SEQUENCE.FRAGMENT_OUTSIDE_GAP,
    );
    if (minStart + SEQUENCE.FRAGMENT_MIN_HEIGHT <= nextEndY) {
      nextStartY = Math.max(nextStartY, minStart);
    }
  }

  if (next) {
    const footprint = estimateFootprint(state.participants, next);
    const maxEnd = next.y - footprint.above - SEQUENCE.FRAGMENT_OUTSIDE_GAP;
    if (maxEnd >= nextStartY + SEQUENCE.FRAGMENT_MIN_HEIGHT) {
      nextEndY = Math.min(nextEndY, maxEnd);
    }
  }

  return { startY: nextStartY, endY: nextEndY };
}

function clampAltDivider(state: FragmentLayoutState, frag: SequenceFragment, scopedMessages: SequenceMessage[]): void {
  if (frag.type !== 'alt') return;
  const minDividerY = frag.startY + SEQUENCE.ALT_DIVIDER_TOP_GAP;
  const maxDividerY = frag.endY - SEQUENCE.ALT_DIVIDER_BOTTOM_GAP;
  if (maxDividerY <= minDividerY) {
    frag.altDividerY = frag.startY + (frag.endY - frag.startY) / 2;
    return;
  }
  const fallback = frag.startY + (frag.endY - frag.startY) / 2;
  const baseDivider = frag.altDividerY ?? fallback;
  let strictMinY = minDividerY;
  let strictMaxY = maxDividerY;
  if (scopedMessages.length > 0) {
    const topBranch = scopedMessages.filter((message) => message.y < baseDivider);
    const bottomBranch = scopedMessages.filter((message) => message.y >= baseDivider);
    if (topBranch.length > 0) {
      const topBranchMaxY = topBranch.reduce((mx, message) => {
        const footprint = estimateFootprint(state.participants, message);
        const y = message.y + footprint.below + SEQUENCE.ALT_DIVIDER_MESSAGE_PADDING;
        return y > mx ? y : mx;
      }, -Infinity);
      strictMinY = Math.max(strictMinY, topBranchMaxY);
    }
    if (bottomBranch.length > 0) {
      const bottomBranchMinY = bottomBranch.reduce((mn, message) => {
        const footprint = estimateFootprint(state.participants, message);
        const y = message.y - footprint.above - SEQUENCE.ALT_DIVIDER_MESSAGE_PADDING;
        return y < mn ? y : mn;
      }, Infinity);
      strictMaxY = Math.min(strictMaxY, bottomBranchMinY);
    }
  }
  if (strictMaxY < strictMinY) {
    const lockY = Math.max(minDividerY, Math.min(maxDividerY, baseDivider));
    strictMinY = lockY;
    strictMaxY = lockY;
  }
  const next = frag.altDividerY ?? fallback;
  frag.altDividerY = Math.max(strictMinY, Math.min(strictMaxY, next));
}

export function recalcFragmentBounds(
  state: FragmentLayoutState,
  fragmentId: string,
  visited: Set<string> = new Set<string>(),
): void {
  if (visited.has(fragmentId)) return;
  visited.add(fragmentId);

  const frag = state.fragments.find((fragment) => fragment.id === fragmentId);
  if (!frag) return;

  const containedMessages = state.messages.filter((message) =>
    isInsideFragmentTree(message.fragmentId, fragmentId, state.fragments),
  );
  const childFragments = state.fragments.filter((child) => child.parentFragmentId === fragmentId);
  const prevStartY = frag.startY;
  const prevAltDividerY = frag.altDividerY;

  let nextStartY: number | undefined;
  let nextEndY: number | undefined;

  if (containedMessages.length > 0) {
    const topPad = fragmentPadTop(frag.type);
    const bottomPad = fragmentPadBottom(frag.type);
    const minY = containedMessages.reduce((mn, message) => {
      const footprint = estimateFootprint(state.participants, message);
      const y = message.y - footprint.above;
      return y < mn ? y : mn;
    }, Infinity);
    const maxY = containedMessages.reduce((mx, message) => {
      const footprint = estimateFootprint(state.participants, message);
      const y = message.y + footprint.below;
      return y > mx ? y : mx;
    }, -Infinity);
    nextStartY = minY - topPad;
    nextEndY = maxY + bottomPad;
  }

  if (childFragments.length > 0) {
    const childStartY = childFragments.reduce(
      (mn, child) => (child.startY < mn ? child.startY : mn),
      Infinity,
    ) - SEQUENCE.FRAGMENT_NEST_PAD;
    const childEndY = childFragments.reduce(
      (mx, child) => (child.endY > mx ? child.endY : mx),
      -Infinity,
    ) + SEQUENCE.FRAGMENT_NEST_PAD;
    nextStartY = nextStartY === undefined ? childStartY : Math.min(nextStartY, childStartY);
    nextEndY = nextEndY === undefined ? childEndY : Math.max(nextEndY, childEndY);
  }

  if (nextStartY !== undefined && nextEndY !== undefined) {
    const clamped = clampFragmentAgainstNeighbors(state, frag, nextStartY, nextEndY);
    frag.startY = clamped.startY;
    frag.endY = Math.max(clamped.endY, clamped.startY + SEQUENCE.FRAGMENT_MIN_HEIGHT);
  } else if (frag.endY - frag.startY < SEQUENCE.FRAGMENT_MIN_HEIGHT) {
    frag.endY = frag.startY + SEQUENCE.FRAGMENT_MIN_HEIGHT;
  }

  if (frag.type === 'alt' && prevAltDividerY !== undefined) {
    const startDelta = frag.startY - prevStartY;
    if (Math.abs(startDelta) >= 0.5) {
      frag.altDividerY = prevAltDividerY + startDelta;
    }
  }

  clampAltDivider(state, frag, containedMessages);
  if (frag.parentFragmentId) {
    recalcFragmentBounds(state, frag.parentFragmentId, visited);
  }
}
